import { ApiError, NetworkError, type ApiErrorBody } from "./errors";
import { baseUrl, type ServiceName } from "./services";
import { isMockMode } from "./mock/mode";
import { mockRequest } from "./mock/router";
import { clearSession } from "../auth/session";
import { freshToken, refreshSession } from "../auth/refresh";

/** Server javob konverti: `{ data, meta }` (dev_spec.md §4.3). */
interface Envelope<T> {
  data: T;
  meta?: { request_id?: string };
}

/** Kursorli ro'yxat (dev_spec.md §11.2: listlarda server filter va cursor). */
export interface PagedList<T> {
  items: T[];
  has_more: boolean;
  next_cursor?: string;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
  /**
   * Takroriy so'rov kaliti (dev_spec.md §4.4).
   *
   * Har bir mutatsiyada MAJBURIY. Berilmasa yangisi hosil
   * qilinadi — tarmoq uzilib, mijoz qayta yuborsa, server
   * ikkinchi amalni bajarmasligi kerak.
   */
  idempotencyKey?: string;
  signal?: AbortSignal;
}

function buildUrl(
  service: ServiceName,
  path: string,
  query?: RequestOptions["query"],
): string {
  const url = new URL(baseUrl(service) + path);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === "") continue;
    url.searchParams.set(key, String(value));
  }
  return url.toString();
}

/**
 * Servisga so'rov yuboradi.
 *
 * Xato konverti TO'LIQ saqlanadi: `allowed_actions` va `details`
 * interfeysga kerak bo'ladi, shuning uchun ular yo'qotilmaydi.
 */
export async function request<T>(
  service: ServiceName,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  return send<T>(service, path, options, false);
}

async function send<T>(
  service: ServiceName,
  path: string,
  options: RequestOptions,
  retried: boolean,
): Promise<T> {
  // Namuna rejimi ENG BOSHIDA tekshiriladi: tarmoqqa umuman
  // chiqmaslik kerak, aks holda serversiz muhitda har bir
  // so'rov taymautni kutib turardi.
  if (isMockMode()) return mockRequest<T>(service, path, options);

  const method = options.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json" };

  const token = await freshToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  if (options.body !== undefined) headers["Content-Type"] = "application/json";

  // Kalit qayta urinishda O'ZGARMAYDI: 401 dan keyingi takror server
  // uchun o'sha amal.
  const idempotencyKey = options.idempotencyKey ?? crypto.randomUUID();
  if (method !== "GET") {
    headers["Idempotency-Key"] = idempotencyKey;
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(service, path, options.query), {
      method,
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (cause) {
    throw new NetworkError(cause);
  }

  const requestId = response.headers.get("X-Request-Id") ?? undefined;

  if (response.status === 204) return undefined as T;

  const raw = await response.text();
  let parsed: unknown = undefined;
  if (raw) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = undefined;
    }
  }

  if (!response.ok) {
    const envelope = parsed as { error?: ApiErrorBody } | undefined;
    const body: ApiErrorBody = envelope?.error ?? {
      code: `http_${response.status}`,
      message: raw.slice(0, 200) || `HTTP ${response.status}`,
    };

    // 401: token bir marta yangilanadi va so'rov takrorlanadi. Kirish
    // va yangilash so'rovlarining o'z 401 i yangilashga olib bormaydi.
    // Yangilab bo'lmasa sessiya tozalanadi: aks holda har bir so'rov
    // 401 bilan qaytib, interfeys sababsiz bo'sh ko'rinardi.
    if (response.status === 401 && token && !retried && !path.startsWith("/api/v1/auth/")) {
      if (await refreshSession(token)) {
        return send<T>(service, path, { ...options, idempotencyKey }, true);
      }
    }
    if (response.status === 401) clearSession();

    throw new ApiError(response.status, body, requestId);
  }

  const envelope = parsed as Envelope<T> | undefined;
  return (envelope?.data ?? (parsed as T)) as T;
}

/** Ro'yxat so'rovi. */
export function list<T>(
  service: ServiceName,
  path: string,
  query?: RequestOptions["query"],
  signal?: AbortSignal,
): Promise<PagedList<T>> {
  return request<PagedList<T>>(service, path, { query, signal });
}
